// /home/labdoo/zitech-nexus/frontend/src/pages/admin/UserForm.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import './UserForm.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Email: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Phone: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Company: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Lock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Role: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="uf-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const [formData, setFormData] = useState({
    email: '',
    phone_number: '',
    full_name: '',
    company_name: '',
    password: '',
    role: 'user',
  });

  const fetchUser = useCallback(async () => {
    try {
      const response = await adminAPI.getUser(parseInt(id!));
      const user = response.data;
      setFormData({
        email: user.email,
        phone_number: user.phone_number,
        full_name: user.full_name,
        company_name: user.company_name || '',
        password: '',
        role: user.role,
      });
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchUser();
    } else {
      setFetching(false);
    }
  }, [id, fetchUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        const updateData: any = {
          full_name: formData.full_name,
          phone_number: formData.phone_number,
          company_name: formData.company_name,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await adminAPI.updateUser(parseInt(id!), updateData);
      } else {
        await adminAPI.createUser(formData);
      }
      navigate('/dashboard/admin/users');
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="uf-loading-container">
        <div className="uf-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="uf-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="uf-loading-text">Loading user details...</p>
      </div>
    );
  }

  return (
    <div className="uf-container">
      {/* Header */}
      <div className="uf-header">
        <div className="uf-header-left">
          <button className="uf-back-btn" onClick={() => navigate('/dashboard/admin/users')}>
            <Icons.Back />
            Back to Users
          </button>
          <h1 className="uf-title">{id ? 'Edit User' : 'Create New User'}</h1>
        </div>
        <button className="uf-btn-secondary" onClick={() => navigate('/dashboard/admin/users')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="uf-form">
        <div className="uf-form-grid">
          {/* Left Column */}
          <div className="uf-form-left">
            <div className="uf-card">
              <h3 className="uf-card-title">
                <Icons.User />
                Personal Information
              </h3>
              
              <div className="uf-form-group">
                <label className="uf-form-label">Full Name *</label>
                <input
                  type="text"
                  required
                  className="uf-form-input"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>

              <div className="uf-form-group">
                <label className="uf-form-label">
                  <Icons.Email />
                  Email *
                </label>
                <input
                  type="email"
                  required
                  className="uf-form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!id}
                  placeholder="Enter email address"
                />
                {id && (
                  <span className="uf-form-hint">Email cannot be changed after creation</span>
                )}
              </div>

              <div className="uf-form-group">
                <label className="uf-form-label">
                  <Icons.Phone />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  className="uf-form-input"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="e.g., +255123456789"
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="uf-form-right">
            <div className="uf-card">
              <h3 className="uf-card-title">
                <Icons.Company />
                Account Settings
              </h3>
              
              <div className="uf-form-group">
                <label className="uf-form-label">
                  <Icons.Company />
                  Company Name
                </label>
                <input
                  type="text"
                  className="uf-form-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Enter company name (optional)"
                />
              </div>

              <div className="uf-form-group">
                <label className="uf-form-label">
                  <Icons.Lock />
                  {id ? 'New Password (optional)' : 'Password *'}
                </label>
                <input
                  type="password"
                  required={!id}
                  className="uf-form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={id ? 'Leave blank to keep current password' : 'Enter password'}
                />
                {id && (
                  <span className="uf-form-hint">Only fill this field if you want to change the password</span>
                )}
              </div>

              <div className="uf-form-group">
                <label className="uf-form-label">
                  <Icons.Role />
                  Role *
                </label>
                <select
                  className="uf-form-select"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                  <option value="trainee">Trainee</option>
                </select>
                <span className="uf-form-hint">Super Admin has full access to all system features</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="uf-form-actions">
          <button 
            type="submit" 
            className="uf-btn-primary" 
            disabled={loading}
          >
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                {id ? 'Update User' : 'Create User'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;